import classnames from 'classnames';
import classNames from 'classnames';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DateTime from '../../helpers/DateTime';
import { NoTopSlot, NoTopSlotRange } from '../../interfaces/NoTopSlot';
import { getIsAllPeriodsSelected } from '../../services/NoTopSlotService';
import Button from '../Button';
import Card from '../Card/Card';
import CheckBoxInput from '../CheckBoxInput/CheckBoxInput';
import { NoSlotButton } from './NoSlotButton';
import { MAX_SLOTS_TO_SHOW } from './NoSlotsConstants';
import style from './NoSlotSelectors.module.scss';

interface NoSlotSelectorsProps {
  noTopSlots: NoTopSlot[];
  updateSlots: (noTopSlots: NoTopSlot[]) => void;
}

const NoSlotSelectors: FC<NoSlotSelectorsProps> = ({
  noTopSlots,
  updateSlots,
}) => {
  const [slotItemPosition, setSlotItemPosition] = useState<number>(0);
  const { t } = useTranslation();

  const setPeriodOfDay = useCallback(({ targetDay, targetPeriod, isEntireDay }: {
    isEntireDay?: boolean;
    targetDay: string;
    targetPeriod?: NoTopSlotRange;
  }): void => {
    const updatedSlots = noTopSlots.map<NoTopSlot>((slot) => {
      const { day, periods } = slot;

      if (day === targetDay) {
        const updatedPeriods = periods.map(period => {
          if (targetPeriod && period.from === targetPeriod.from && period.to === targetPeriod.to) {
            return { ...period, selected: targetPeriod.selected };
          } else if (isEntireDay !== undefined) {
            return { ...period, selected: isEntireDay };
          }

          return period;
        });

        return { ...slot, periods: updatedPeriods };
      }

      return slot;
    });

    updateSlots(updatedSlots);
  }, [noTopSlots, updateSlots]);

  const renderArrow = useCallback((arrow: 'left' | 'right') => {
    const isLeftArrow = arrow === 'left';

    const handleOnClick = () => {
      const updatedSlots: NoTopSlot[] = noTopSlots.map<NoTopSlot>((slot: NoTopSlot) => {
        const { periods } = slot;
        const updatedPeriods = periods.map(period => ({ ...period, selected: false }));

        return { ...slot, periods: updatedPeriods };
      });

      updateSlots(updatedSlots);

      setSlotItemPosition((prevValue) => {
        return isLeftArrow
          ? prevValue - MAX_SLOTS_TO_SHOW
          : prevValue + MAX_SLOTS_TO_SHOW;
      });
    };

    return (
      <Button
        ariaLabel={t(`slots:show${isLeftArrow ? 'Previous' : 'Next'}Slots`)}
        icon={{ name: isLeftArrow ? 'arrow_back_ios' : 'arrow_forward_ios' }}
        onClick={handleOnClick}
        size="small"
      />
    );
  }, [t, noTopSlots, updateSlots]);

  const renderedSlotColumns = useMemo(() => {
    if (!noTopSlots.length) return null;

    const slotsToDisplay = noTopSlots.slice(slotItemPosition, slotItemPosition + MAX_SLOTS_TO_SHOW);

    return (
      <>
        {slotsToDisplay.map(({ day, allowed, periods }) => {
          const areAllPeriodsSelected = getIsAllPeriodsSelected(periods);

          return (
            <div
              className={classNames(
                style.wrapper,
                areAllPeriodsSelected && 'partner-initial-border-color-rounded',
                !areAllPeriodsSelected && style.border
              )}
              key={day}
            >
              <div
                className={classNames(style.noSlot, 'background-color')}
              >
                <span className={classnames(style.label)}>
                  <span className={style.date}>
                    {DateTime.formatDate('EEEE', new Date(day))}
                  </span>
                  <span>{DateTime.formatDate('dd LLLL', new Date(day))}</span>
                </span>

                <div className={style.noTopSlotPeriods}>
                  {!!periods.length &&
                    periods.map(({ from, to, selected }) => (
                      <NoSlotButton
                        className={style.noSlotButton}
                        disabled={!allowed}
                        key={from}
                        onSelect={(isSelected: boolean): void => {
                          setPeriodOfDay({
                            targetDay: day,
                            targetPeriod: { from, to, selected: isSelected },
                          });
                        }}
                        selected={selected}
                        text={`${DateTime.getTimeStringFromDate(
                          DateTime.roundToNearestMinutes(from)
                        )}
                      - ${DateTime.getTimeStringFromDate(
                        DateTime.roundToNearestMinutes(to)
                      )}`}
                      />
                    ))}
                </div>
                <CheckBoxInput
                  className={classNames(style.entireDay, style.checkbox)}
                  disabled={!allowed}
                  id={day}
                  isChecked={areAllPeriodsSelected}
                  label={t('noslots:entireDay')}
                  onCheck={(isChecked) => {
                    setPeriodOfDay({ targetDay: day, isEntireDay: isChecked });
                  }}
                  smallLabel
                />
              </div>
            </div>
          );
        })}
      </>
    );
  }, [noTopSlots, setPeriodOfDay, slotItemPosition, t]);

  return (
    <div className={style.cardContainer}>
      <Card className={style.root}>{renderedSlotColumns}</Card>
      <div className={style.leftArrowContainer}>
        {slotItemPosition >= MAX_SLOTS_TO_SHOW && renderArrow('left')}
      </div>
      <div className={style.rightArrowContainer}>
        {slotItemPosition + MAX_SLOTS_TO_SHOW < noTopSlots.length &&
          renderArrow('right')}
      </div>
    </div>
  );
};

export default NoSlotSelectors;
